// ============================================================================
// Confirm Closure Review Modal
// ============================================================================
// Supervisor confirms that a closed early-recovery case is properly closed.
// Optional remarks (max 2000 chars). Marks review_status → CONFIRMED.

import React, { useCallback, useEffect, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import notificationService from 'services/notificationService';
import { useConfirmClosureReview } from 'hooks/api/usePreLitigation';
import type { ClosedCaseReviewRow } from 'api/types/preLitigation.types';

interface Props {
  isOpen: boolean;
  row: ClosedCaseReviewRow | null;
  onClose: () => void;
  onSuccess: () => void;
}

const ConfirmClosureReviewModal: React.FC<Props> = ({ isOpen, row, onClose, onSuccess }) => {
  const [remarks, setRemarks] = useState('');
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const { run, loading } = useConfirmClosureReview();

  useEffect(() => {
    if (isOpen) setRemarks('');
  }, [isOpen]);

  const handleConfirmSave = useCallback(async () => {
    if (!row) return;
    setSaveConfirmOpen(false);
    try {
      await run(row.id, { reviewRemarks: remarks.trim() || undefined });
      notificationService.toast.success(`Case ${row.caseCode} closure confirmed.`);
      onSuccess();
      onClose();
    } catch {
      notificationService.toast.error('Failed to confirm closure. Please try again.');
    }
  }, [row, remarks, run, onSuccess, onClose]);

  if (!isOpen || !row) return null;

  return (
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div className="modal-container modal-md">
        {loading && (
          <div style={{
            position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            zIndex: 10, borderRadius: 'inherit', gap: '0.75rem',
          }}>
            <div style={{
              width: '2rem', height: '2rem', border: '3px solid var(--border-color)',
              borderTopColor: '#16a34a', borderRadius: '50%', animation: 'spin 0.8s linear infinite',
            }} />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Confirming...</span>
          </div>
        )}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Confirm Closure Review</h2>
          <button className="modal-close" onClick={onClose} disabled={loading}><IconSystem.x size={20} /></button>
        </div>

        <div className="modal-content">
          <div style={{
            padding: '0.75rem 1rem',
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border-color)',
            borderRadius: '6px',
            marginBottom: '1rem',
            fontSize: '0.85rem',
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
              <div><strong>Case:</strong> {row.caseCode}</div>
              <div><strong>Customer:</strong> {row.customerName}</div>
              <div><strong>Account:</strong> {row.accountNumber}</div>
              <div><strong>Branch:</strong> {row.branchCode ?? '—'}</div>
              <div style={{ gridColumn: '1 / -1' }}>
                <strong>Closure reason:</strong> {row.closureReasonName}{' '}
                <span style={{
                  fontSize: '0.72rem', fontWeight: 600,
                  padding: '2px 6px', borderRadius: '999px',
                  color: row.outcomeCategory === 'FAVOURABLE' ? '#16a34a' : row.outcomeCategory === 'UNFAVOURABLE' ? '#dc2626' : '#6b7280',
                  border: `1px solid ${row.outcomeCategory === 'FAVOURABLE' ? '#16a34a' : row.outcomeCategory === 'UNFAVOURABLE' ? '#dc2626' : '#6b7280'}`,
                  marginLeft: '0.5rem',
                }}>{row.outcomeCategory}</span>
              </div>
              {row.closureRemarks && (
                <div style={{ gridColumn: '1 / -1', color: 'var(--text-secondary)' }}>
                  <strong style={{ color: 'var(--text-primary)' }}>Closure remarks:</strong> {row.closureRemarks}
                </div>
              )}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Review Remarks <span style={{ color: 'var(--text-secondary)', fontWeight: 400 }}>(optional)</span></label>
            <textarea
              className="form-textarea"
              rows={4}
              value={remarks}
              maxLength={2000}
              placeholder="Optional notes for the audit trail..."
              onChange={(e) => setRemarks(e.target.value)}
            />
            <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', textAlign: 'right', marginTop: '0.25rem' }}>
              {remarks.length}/2000
            </div>
          </div>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={loading}>Cancel</button>
          <button className="btn btn-primary" onClick={() => setSaveConfirmOpen(true)} disabled={loading}>
            Confirm Review
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={saveConfirmOpen}
        title="Confirm Closure Review"
        message={`Mark case ${row.caseCode} as reviewed and confirmed?`}
        confirmLabel="Confirm"
        cancelLabel="Cancel"
        variant="success"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />
    </>
  );
};

export default ConfirmClosureReviewModal;
